'use client';

import { c as _c } from "react/compiler-runtime";
import { useUser } from '@clerk/nextjs';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button, DialogTrigger } from 'react-aria-components';
import { Popover } from '~/components/Popover';
import { useIsNavDesktop } from '~/hooks/useIsNavDesktop';
import { AccountMenu } from './AccountMenu';
import { AccountPanel } from './AccountPanel';
import { UserAvatar } from './UserAvatar';
import styles from './UserMenu.module.css';
import { getUserShortName } from './userIdentity';
type UserMenuProps = {
  /**
   * Open state is controlled by Navigation, because the mobile and desktop badges are separate
   * mounts in separate containers: state held in here would reset when a resize across the nav
   * breakpoint swapped one for the other, closing an open menu and dropping focus. Left
   * uncontrolled the component still works standalone.
   */
  readonly isOpen?: boolean;
  readonly onOpenChange?: (isOpen: boolean) => void;
};
export const UserMenu = t0 => {
  const $ = _c(44);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isOpen: controlledIsOpen,
    onOpenChange
  } = t1;
  const {
    user
  } = useUser();
  const t = useTranslations("account");
  const isDesktop = useIsNavDesktop();
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(false);
  const isOpen = controlledIsOpen ?? uncontrolledIsOpen;
  const setIsOpen = onOpenChange ?? setUncontrolledIsOpen;
  if (!user) {
    return null;
  }
  let t2;
  if ($[2] !== t || $[3] !== user) {
    t2 = t("menu_trigger_label", {
      name: getUserShortName(user)
    });
    $[2] = t;
    $[3] = user;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const triggerLabel = t2;
  let t3;
  if ($[5] !== setIsOpen) {
    t3 = () => setIsOpen(false);
    $[5] = setIsOpen;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const close = t3;
  let t4;
  if ($[7] !== user) {
    t4 = <UserAvatar user={user} />;
    $[7] = user;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const avatar = t4;
  if (!isDesktop) {
    let t5;
    if ($[9] !== setIsOpen) {
      t5 = () => setIsOpen(true);
      $[9] = setIsOpen;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== avatar || $[12] !== isOpen || $[13] !== t5 || $[14] !== triggerLabel) {
      t6 = <button type="button" className={styles.trigger} aria-label={triggerLabel} aria-haspopup="dialog" aria-expanded={isOpen} onClick={t5} data-test-id="user-menu-trigger">{avatar}</button>;
      $[11] = avatar;
      $[12] = isOpen;
      $[13] = t5;
      $[14] = triggerLabel;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    let t7;
    if ($[16] !== t) {
      t7 = t("menu_label");
      $[16] = t;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    let t8;
    if ($[18] !== close) {
      t8 = <AccountMenu onNavigate={close} variant="panel" />;
      $[18] = close;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    let t9;
    if ($[20] !== close || $[21] !== isOpen || $[22] !== t7 || $[23] !== t8) {
      t9 = <AccountPanel ariaLabel={t7} isOpen={isOpen} close={close}>{t8}</AccountPanel>;
      $[20] = close;
      $[21] = isOpen;
      $[22] = t7;
      $[23] = t8;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== t6 || $[26] !== t9) {
      t10 = <>{t6}{t9}</>;
      $[25] = t6;
      $[26] = t9;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    return t10;
  }
  let t5;
  if ($[28] !== avatar || $[29] !== triggerLabel) {
    t5 = <Button className={styles.trigger} aria-label={triggerLabel} data-test-id="user-menu-trigger">{avatar}</Button>;
    $[28] = avatar;
    $[29] = triggerLabel;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  let t6;
  if ($[31] !== t) {
    t6 = t("menu_label");
    $[31] = t;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  let t7;
  if ($[33] !== close) {
    t7 = <AccountMenu onNavigate={close} variant="popover" />;
    $[33] = close;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  let t8;
  if ($[35] !== setIsOpen || $[36] !== t6 || $[37] !== t7) {
    t8 = <Popover placement="bottom end" variant="menu" ariaLabel={t6} onOpenChange={setIsOpen}>{t7}</Popover>;
    $[35] = setIsOpen;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
  } else {
    t8 = $[38];
  }
  let t9;
  if ($[39] !== isOpen || $[40] !== setIsOpen || $[41] !== t5 || $[42] !== t8) {
    t9 = <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>{t5}{t8}</DialogTrigger>;
    $[39] = isOpen;
    $[40] = setIsOpen;
    $[41] = t5;
    $[42] = t8;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  return t9;
};